import Head from "next/head";
import { StaticPageLayout } from "@/components/StaticPageLayout";
import {
  Button,
  Container,
  Flex,
  MantineProvider,
  Select,
  TextInput,
  Textarea,
  createTheme,
  Text,
  Stack,
  rem,
  Divider,
  Box,
  Group,
  Alert,
  Checkbox,
  Center,
} from "@mantine/core";
import { GetServerSideProps } from "next";
import { Trans, useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useForm } from "@mantine/form";
import { IconPhone } from "@tabler/icons-react";
import { Layout } from "@/components/Layout";
import { useState } from "react";
import { submitContactForm } from "@/api";
import { IContactFormRequest } from "@/types";
import { APP_URL, RESPONSE_CODE } from "@/constants";
import Link from "next/link";
import { generateJsonLd, getCurrentLocaleUrl } from "@/utils";

export default function Contact(): JSX.Element {
  const { t, i18n, ready } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean | null>(null);
  const [isPolicyChecked, setIsPolicyChecked] = useState<boolean>(false);

  const subjects = t("contact:subject.options", { returnObjects: true }) as any;
  const breadcrumbs = [
    { title: t("common:service.title"), href: "/" },
    { title: t("common:links.contact"), href: "" },
  ];
  const initialFormValue = {
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    orderId: "",
    message: "",
  };

  const form = useForm({
    initialValues: initialFormValue,

    validate: (values) => ({
      firstName:
        values.firstName === ""
          ? t("contact:first_name.validations.required")
          : null,
      lastName:
        values.lastName === ""
          ? t("contact:last_name.validations.required")
          : null,
      email: /^\S+@\S+$/.test(values.email)
        ? null
        : t("contact:email.validations.format_invalid"),
      subject:
        values.subject === ""
          ? t("contact:subject.validations.required")
          : null,
      message:
        values.message === ""
          ? t("contact:message.validations.required")
          : null,
    }),
  });

  const theme = createTheme({
    components: {
      TextInput: {
        defaultProps: {
          classNames: {
            input: "p-4 h-12 border-dark-8",
            label: "font-normal",
          },
        },
      },
      Select: {
        defaultProps: {
          classNames: {
            input: "p-4 h-12 border-dark-8",
            label: "font-normal",
          },
        },
      },
      Textarea: {
        defaultProps: {
          autosize: true,
          minRows: 4,
          classNames: {
            input: "p-4 border-dark-8",
            label: "font-normal",
          },
        },
      },
    },
  });

  const handleSubmission = (values: IContactFormRequest) => {
    setIsLoading(true);
    try {
      submitContactForm({ ...values, lang: i18n.language })
        .then((res) => {
          setIsLoading(false);
          if (res.status === RESPONSE_CODE.OK) {
            form.setValues(initialFormValue);
            setIsSuccess(true);
          } else {
            setIsSuccess(false);
          }
        })
        .catch((err) => {
          setIsSuccess(false);
        });
    } catch (err) {}
  };

  const jsonLdContactPage = generateJsonLd("ContactPage", i18n.language, {
    url: `${APP_URL}${getCurrentLocaleUrl(i18n.language)}/contact`,
    mainEntity: {
      "@type": "Organization",
      name: "Japan Bullet Train",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+81-3-6820-0074",
        contactType: "Customer Service",
        areaServed: "JP",
        availableLanguage: [
          "English",
          "Chinese (Simplified)",
          "Korean",
          "Japanese",
          "Indonesian",
          "Spanish",
          "French",
          "Filipino/Tagalog",
          "Vietnamese",
        ],
        openingHoursSpecification: {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ],
          opens: "08:00",
          closes: "23:00",
        },
      },
    },
  });

  return (
    <>
      <Head>
        <title>{t("meta:contact.title")}</title>
        <meta content={t("meta:contact.description")} name="description" />
        <meta content="width=device-width, initial-scale=1" name="viewport" />
        <link href="/favicon.ico" rel="icon" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLdContactPage),
          }}
        />
      </Head>
      <Layout footer={{ breadcrumbs: breadcrumbs }}>
        <StaticPageLayout title={t("common:links.contact")} />
        <Container size="xs" py={rem(20)}>
          <Text my={rem(20)}>{t("contact:note")}</Text>
          <MantineProvider theme={theme}>
            <form
              onSubmit={form.onSubmit((values) => handleSubmission(values))}
            >
              <Stack gap="md">
                <Flex gap="md" w="100%">
                  <TextInput
                    withAsterisk
                    label={t("contact:first_name.label")}
                    placeholder={t("contact:first_name.placeholder")}
                    className="w-1/2"
                    {...form.getInputProps("firstName")}
                  />
                  <TextInput
                    withAsterisk
                    label={t("contact:last_name.label")}
                    placeholder={t("contact:last_name.placeholder")}
                    className="w-1/2"
                    {...form.getInputProps("lastName")}
                  />
                </Flex>
                <TextInput
                  withAsterisk
                  label={t("contact:email.label")}
                  placeholder={t("contact:email.placeholder")}
                  {...form.getInputProps("email")}
                />
                {ready && (
                  <Select
                    withAsterisk
                    label={t("contact:subject.label")}
                    placeholder={t("contact:subject.placeholder")}
                    data={subjects}
                    {...form.getInputProps("subject")}
                  />
                )}
                <TextInput
                  label={t("contact:order_id.label")}
                  placeholder={t("contact:order_id.placeholder")}
                  {...form.getInputProps("orderId")}
                />
                <Textarea
                  withAsterisk
                  label={t("contact:message.label")}
                  placeholder={t("contact:message.placeholder")}
                  {...form.getInputProps("message")}
                />
                <Center>
                  <Checkbox
                    onChange={() => setIsPolicyChecked(!isPolicyChecked)}
                    pb="xs"
                    pt="md"
                    label={
                      <Trans
                        i18nKey="contact:checkbox"
                        components={{
                          url: (
                            <Link
                              locale={i18n.language}
                              target="_blank"
                              className="underline text-custom-link underline-offset-4 decoration-1"
                              href="https://www.inbound-platform.com/en/privacy/"
                              rel="noopener"
                            />
                          ),
                        }}
                      />
                    }
                  />
                </Center>
                <Button
                  variant="filled"
                  size="md"
                  h={rem(60)}
                  className="uppercase disabled:bg-dark-5 disabled:text-dark-6"
                  type="submit"
                  loading={isLoading}
                  disabled={!isPolicyChecked}
                >
                  {t("contact:submit")}
                </Button>
              </Stack>
            </form>
            {isSuccess === true && (
              <Alert
                variant="light"
                color="green"
                withCloseButton
                mt="lg"
                title={t("contact:submit_validations.success.label")}
              >
                {t("contact:submit_validations.success.description")}
              </Alert>
            )}
            {isSuccess === false && (
              <Alert
                variant="light"
                color="red"
                withCloseButton
                mt="lg"
                title={t("contact:submit_validations.error.label")}
              >
                {t("contact:submit_validations.error.description")}
              </Alert>
            )}
          </MantineProvider>
          <Divider my="xl" label="OR" labelPosition="center" />
          <Box
            bg="blue.7"
            className="rounded-lg shadow-sm"
            py={rem(30)}
            px={{ base: rem(40), md: rem(80) }}
            mb={rem(50)}
          >
            <Stack gap="xs" justify="center">
              <Text c="white" ta="center">
                {t("common:navbar.contact_note")}
              </Text>
              <Link href={`tel:${t("common:service.contact")}`}>
                <Box bg="blue.9" className="rounded-md" py="md">
                  <Group gap={rem(8)} justify="center">
                    <IconPhone className="text-white" height={20} width={20} />
                    <Text c="white" size="lg" fw={700}>
                      {t("common:service.contact")}
                    </Text>
                  </Group>
                </Box>
              </Link>
              <Text c="white" size="xs" ta="center">
                {t("common:service.business_hours_label")}:{" "}
                {t("common:service.business_hours")}
              </Text>
            </Stack>
          </Box>
        </Container>
      </Layout>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  const translations = await serverSideTranslations(locale ?? "en", [
    "meta",
    "common",
    "contact",
  ]);

  return {
    props: {
      ...translations,
    },
  };
};
